import { cloneDeep } from 'lodash';

import { KEY_CODES } from '@/utils/constants';
import { generateUniqueId } from '@/utils/helper';

export default {
  name: 'MenuItemOptionItemsViewer',
  props: {
    option: {
      required: true,
      type: Object,
    },
  },
  data() {
    return {
      isDirty: false,
      items: [],
      max: 0,
      min: 0,
      name: '',
      newItems: [],
      note: '',
      removeItemsIndexes: [],
    };
  },
  computed: {
    disableConfirmBtn() {
      const withoutAnyItems = !this.newItems?.length && !this.items?.length;
      return withoutAnyItems || this.hasEmptyItems || !this.name;
    },
    hasEmptyItems() {
      return this.newItems?.some(({ name }) => !name);
    },
    minMax() {
      return this.max || 99;
    },
    optionIndex() {
      return this.option?.index;
    },
    totalItemsLength() {
      return this.items?.length + this.newItems?.length;
    },
  },
  created() {
    this.setDefaultDatum();
  },
  beforeDestroy() {
    Object.assign(this.$data, this.$options.data.apply(this));
  },
  watch: {
    max(newVal, oldVal) {
      if (newVal !== oldVal) {
        if (this.min > +newVal) {
          this.min = +newVal;
        }
        if (+newVal !== this.option?.rules?.max) {
          this.toggleDirty(true);
        }
      }
    },
    min(newVal, oldVal) {
      if (newVal !== oldVal && +newVal !== this.option?.rules?.min) {
        this.toggleDirty(true);
      }
    },
    name(newVal, oldVal) {
      if (newVal !== oldVal && newVal !== this.option?.name) {
        this.toggleDirty(true);
      }
    },
    note(newVal, oldVal) {
      if (newVal !== oldVal && newVal !== this.option?.storeNote) {
        this.toggleDirty(true);
      }
    },
    totalItemsLength(newVal, oldVal) {
      if (newVal !== oldVal && this.max > newVal) {
        this.max = newVal;
      }
    },
  },
  methods: {
    onAddNewItem() {
      this.toggleDirty(true);
      const index = generateUniqueId();
      this.newItems.push({
        index,
        name: '',
        price: null,
      });
    },
    onCheckAndAddNewItem($event, viewIndex, key) {
      if (this.hasEmptyItems) {
        return;
      }

      const isLastItem = viewIndex + 1 === this.newItems?.length;
      if (isLastItem) {
        this.onAddNewItem(viewIndex);
      }
      this.onFocusNext($event, viewIndex, key);
    },
    onFocus($event, key) {
      const {
        isComposing,
        keyCode,
        target: {
          selectionEnd,
          value,
        },
      } = $event;
      if (keyCode === KEY_CODES.LEFT && Number.isInteger(selectionEnd) && selectionEnd >= 0) {
        // eslint-disable-next-line no-param-reassign
        $event.target.selectionStart = selectionEnd - 1;
        // eslint-disable-next-line no-param-reassign
        $event.target.selectionEnd = selectionEnd - 1;
        return;
      }
      if (
        keyCode === KEY_CODES.RIGHT
        && Number.isInteger(selectionEnd)
        && selectionEnd < value.length
      ) {
        // eslint-disable-next-line no-param-reassign
        $event.target.selectionStart = selectionEnd + 1;
        // eslint-disable-next-line no-param-reassign
        $event.target.selectionEnd = selectionEnd + 1;
        return;
      }
      if (!key || isComposing) {
        return;
      }
      this.$nextTick(() => {
        this.$refs[key][0].focus();
      });
    },
    onFocusNext($event, viewIndex = 0, key) {
      const isLastItem = this.newItems?.length === viewIndex + 1;
      if (!key || isLastItem || $event?.isComposing) {
        return;
      }
      const index = this.newItems[viewIndex + 1]?.index;
      this.onFocusToIndex($event, key, index);
    },
    onFocusPre($event, viewIndex = 0, key) {
      const isFirstItem = viewIndex === 0;
      if (!key || isFirstItem || $event?.isComposing) {
        return;
      }
      const index = this.newItems[viewIndex - 1]?.index;
      this.onFocusToIndex($event, key, index);
    },
    onFocusToIndex($event, key, index) {
      if (!key || !Number.isInteger(index)) {
        return;
      }
      this.onFocus($event, `${key}-${index}`);
    },
    onRemoveOriginItem(viewIndex, itemIndex) {
      this.toggleDirty(true);
      this.items.splice(viewIndex, 1);
      this.removeItemsIndexes.push(itemIndex);
    },
    onRemoveItem(index) {
      this.newItems.splice(index, 1);
      const isDirty = !!this.newItems?.length;
      this.toggleDirty(isDirty);
    },
    onResetDatum() {
      this.setDefaultDatum();
      this.toggleDirty(false);
    },
    onSaveOptionWithItems() {
      const newItems = this.newItems?.map(
        ({ price, ...props }) => ({ price: +price || 0, ...props }),
      ) || [];
      this.$emit('modify-option-with-items', {
        max: this.max,
        min: this.min,
        name: this.name,
        newItems,
        note: this.note,
        removeItemsIndexes: this.removeItemsIndexes,
      });
      this.setDefaultDatum();
      this.toggleDirty(false);
    },
    setDefaultDatum() {
      this.items = cloneDeep(this.option?.items || []);
      this.max = Number.isInteger(this.option?.rules?.max) ? this.option?.rules?.max : 0;
      this.min = Number.isInteger(this.option?.rules?.min) ? this.option?.rules?.min : 0;
      this.name = this.option?.name || '';
      this.newItems = [];
      this.note = this.option?.storeNote || '';
      this.removeItemsIndexes = [];
    },
    toggleDirty(isDirty = false) {
      this.isDirty = isDirty;
    },
  },
};
